import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, CheckCircle, Clock, Wrench } from 'lucide-react';

const WorkProgressDisplay = ({ report }) => {
  const { t } = useTranslation();

  if (!report.vendor || !report.vendor.show_publicly) {
    return null;
  }

  const getStatusBadge = (status) => {
    const badges = {
      started: { color: 'bg-blue-100 text-blue-800', icon: <Wrench size={14} /> },
      in_progress: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock size={14} /> },
      completed: { color: 'bg-green-100 text-green-800', icon: <CheckCircle size={14} /> }
    };
    
    return badges[status] || badges.in_progress;
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-md">
      <h2 className="text-xl font-bold mb-4 flex items-center">
        <Wrench size={24} className="mr-2 text-cyan-600" />
        {t('work.workProgress')}
      </h2>

      {/* Vendor Details */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold mb-2">{t('work.vendorDetails')}</h3>
        <div className="space-y-1 text-sm">
          <p><strong>{t('work.vendorName')}:</strong> {report.vendor.name}</p>
          {report.work_start_date && (
            <p className="flex items-center gap-2">
              <Calendar size={14} />
              <strong>{t('work.assignedOn')}:</strong> {new Date(report.work_start_date).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Work Timeline */}
      {report.work_updates && report.work_updates.length > 0 ? (
        <div className="space-y-4">
          <h3 className="font-semibold mb-3">{t('work.timeline')}</h3>
          {report.work_updates.map((update, idx) => {
            const badge = getStatusBadge(update.status);
            return (
              <div key={idx} className="border-l-4 border-cyan-500 pl-4 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color} flex items-center gap-1`}>
                    {badge.icon}
                    {t(`work.${update.status}`)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(update.date).toLocaleString()}
                  </span>
                </div>
                <p className="text-gray-700 mb-2">{update.description}</p>
                {update.photos && update.photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {update.photos.map((photo, pidx) => (
                      <img
                        key={pidx}
                        src={photo}
                        alt={`Progress ${pidx + 1}`}
                        className="w-full h-24 object-cover rounded"
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-500 text-center py-4">{t('work.noUpdates')}</p>
      )}

      {/* Final Completion Photos */}
      {report.status === 'completed' && report.final_completion_photos && report.final_completion_photos.length > 0 && (
        <div className="mt-6 pt-6 border-t">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CheckCircle size={18} className="text-green-600" />
            {t('work.finalPhotos')}
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {report.final_completion_photos.map((photo, idx) => (
              <img
                key={idx}
                src={photo}
                alt={`Final ${idx + 1}`}
                className="w-full h-32 object-cover rounded"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkProgressDisplay;
